import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import differenceInYears from 'date-fns/difference_in_years'
import {
  branch,
  compose,
  renderComponent,
  withHandlers,
  withProps,
  withPropsOnChange,
  withState,
} from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import withRemoteStrings from 'components/HOCs/withRemoteStrings'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ProductKey } from 'generated/types'
import userQuery from 'src/graphql/queries/userDetails.query.gql'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { withEnv } from 'utils/environment'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { withOptimizely } from 'utils/Optimizely'
import { withPrompt } from 'utils/prompts'
import withTieredRegistration from 'utils/TieredRegistration'

import tierSubscriptionQuery from './gql/tierSubscription.query.gql'
import { Props, TierGqlProps, TierGqlResp, UserGqlProps, UserGqlResp } from './types'
import { unverifiedUserPrompts } from './unverifiedUser.prompts'

const MAX_AGE = 69
const US_TERRITORIES = ['Puerto Rico', 'Guam', 'U.S. Virgin Islands']

const getSubscriptionString = (
  tierPrice,
  hasSpendAccount,
  isTieredRegEnabled,
  subscriptionIncludesLater,
  getRemoteString,
) => {
  if (isTieredRegEnabled) {
    return subscriptionIncludesLater
      ? getRemoteString('later_welcome_footer_tier_included', 'Included in your subscription')
      : `Upgrade to the Personal tier for $${tierPrice}/month`
  } else {
    return hasSpendAccount
      ? getRemoteString('later_welcome_footer_tier_included', 'Included in your subscription')
      : getRemoteString('later_welcome_footer', 'Get Acorns + Acorns Later for just $2 a month')
  }
}
export const enhance = compose<Props, {}>(
  withEnv,
  withFlowRouter,
  withRouter,
  withOptimizely,
  withPrompt,
  withRemoteStrings,
  withTieredRegistration,
  graphql<{}, TierGqlResp, {}, TierGqlProps>(tierSubscriptionQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
    props: ({ data: { tierSubscriptionForUser, loading } }: any) => ({
      includedProducts: tierSubscriptionForUser?.tier?.includedProducts ?? [],
      currentTierPrice: tierSubscriptionForUser?.tierPrice?.amount?.value,
      currentTierPriceId: tierSubscriptionForUser?.tierPrice?.id,
      loading,
    }),
  }),
  graphql<{}, UserGqlResp, {}, UserGqlProps>(userQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
    props: ({ data: { user, allBankAccounts, loading } }) => ({
      dob: user?.dob ?? null,
      state: user?.state ?? null,
      hasSpendAccount: allBankAccounts?.length > 0 ?? false,
      loading,
      alerts: user?.alerts ?? [],
      verified: user?.verified ?? false,
    }),
  }),
  withState('hasOutsideIncome', 'setHasOutsideIncome', true),
  withState('outsideIncomeEligible', 'setOutsideIncomeEligible', true),
  withState('showModals', 'setShowModals', false),
  withState('isSubmitting', 'setIsSubmitting', false),
  withProps(
    ({
      loading,
      dob,
      includedProducts,
      isTieredRegEnabled,
      hasSpendAccount,
      getRemoteString,
      tierPrice,
      alerts,
      verified,
    }) => {
      if (!loading) {
        const birthDate = new Date(dob)
        const currentDate = new Date()
        const ageUnder69 = differenceInYears(currentDate, birthDate) < MAX_AGE
        const subscriptionIncludesLater = includedProducts.some(
          product => product.key === ProductKey.Later,
        )

        return {
          loading,
          subscriptionIncludesLater,
          upgradeEligible: isTieredRegEnabled && !subscriptionIncludesLater,
          retirementEligible: ageUnder69,
          title: subscriptionIncludesLater
            ? getRemoteString('later_welcome_title_included', 'Welcome to your Later account')
            : getRemoteString('later_welcome_title', 'An easy way to save for retirement'),
          subscription: getSubscriptionString(
            tierPrice,
            hasSpendAccount,
            isTieredRegEnabled,
            subscriptionIncludesLater,
            getRemoteString,
          ),
          subscriptionDetails: getRemoteString(
            'later_welcome_footer_tier_subtext',
            'We just have a few simple questions to choose the perfect type of retirement account for you.',
          ),
          subTextOne: getRemoteString(
            'later_welcome_subtext_one',
            'Set up automatic, Recurring Contributions',
          ),
          subTextTwo: getRemoteString(
            'later_welcome_subtext_two',
            'We will recommend an IRA type for you',
          ),
          subTextThree: getRemoteString(
            'later_welcome_subtext_three',
            'Age-based portfolio that meets your needs',
          ),
          cta: subscriptionIncludesLater
            ? getRemoteString('later_welcome_tier_cta', 'Next')
            : getRemoteString('later_welcome_cta', 'Get Started'),
          userVerified: verified,
        }
      } else {
        return {
          loading,
        }
      }
    },
  ),
  withPropsOnChange(['state'], ({ setOutsideIncomeEligible, state, retirementEligible }) => {
    setOutsideIncomeEligible(US_TERRITORIES.includes(state) && retirementEligible)
  }),
  withHandlers({
    upgradeUserToSubscription: ({
      subscribeUserToTier,
      route,
      setShowModals,
      setIsSubmitting,
      tierPriceId,
      currentTierPriceId,
    }) => () => {
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.WELCOME.LATER_ONBOARDING_MODAL_CONFIRM_BUTTON),
          ...getDefaultEventData('Button Tapped'),
          current_subscription: currentTierPriceId,
        },
      })
      setShowModals(false)
      setIsSubmitting(true)
      subscribeUserToTier()(tierPriceId)
        .then(() => {
          route({ from: FlowRoute.Welcome, action: 'continue' })
        })
        .catch(() => {
          setIsSubmitting(false)
        })
    },
    closeUpgradeUserToSubscription: ({ setShowModals, currentTierPriceId }) => () => {
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.WELCOME.LATER_ONBOARDING_MODAL_CANCEL_BUTTON),
          ...getDefaultEventData('Button Tapped'),
          current_subscription: currentTierPriceId,
        },
      })
      setShowModals(false)
    },
    handleRedirect: ({ env }) => () => {
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.WELCOME.LATER_ONBOARDING_UNQUALIFIED_CANCEL_BUTTON),
          ...getDefaultEventData('Button Tapped'),
        },
      }),
        window.location.replace(`${env.get('webAppUrl')}`)
    },
    handleLearnMore: ({ getRemoteString }) => () => {
      const url = getRemoteString(
        'later_welcome_us_territories_unqualified_modal_learn_more_url',
        'https://www.acorns.com/support/acorns-later/can-i-have-an-acorns-later-account-if-i-live-in-a-us-territory-/',
      )
      window.location.replace(url.props.source)
    },
    handleOutsideIncomeEligibility: ({
      setHasOutsideIncome,
      setOutsideIncomeEligible,
      upgradeEligible,
      route,
    }) => () => {
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.WELCOME.LATER_ONBOARDING_UNQUALIFIED_CONFIRM_BUTTON),
          ...getDefaultEventData('Button Tapped'),
        },
      }),
        setOutsideIncomeEligible(false)
      setHasOutsideIncome(true)
      if (!upgradeEligible) {
        route({ from: FlowRoute.Welcome, action: 'continue' })
      }
    },
    handleOutsideIncomeEligibilityClose: ({ setHasOutsideIncome }) => () => {
      setHasOutsideIncome(false)
    },
    checkIfEligibleForLater: ({
      route,
      retirementEligible,
      outsideIncomeEligible,
      upgradeEligible,
      setShowModals,
      userVerified,
      promptUser,
      env,
    }) => async () => {
      if (!userVerified) {
        const { index } = await promptUser(unverifiedUserPrompts.get('verificationRequired'))
        if (index === 1) {
          const url = env.get('webAppUrl') + '/document-uploader'
          window.location.replace(url)
        }
      } else if (!retirementEligible || outsideIncomeEligible || upgradeEligible) {
        return setShowModals(true)
      } else {
        route({ from: FlowRoute.Welcome, action: 'continue' })
      }
    },
    onRequestClose: ({ setShowModals }) => () => {
      setShowModals(false)
    },
  }),
  withHandlers({
    handleGetStarted: ({ checkIfEligibleForLater, currentTierPriceId }) => () => {
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.WELCOME.LATER_ONBOARDING_NEXT_BUTTON),
          ...getDefaultEventData('Button Tapped'),
          current_subscription: currentTierPriceId,
        },
      })
      checkIfEligibleForLater()
    },
  }),
  branch(({ loading }) => loading, renderComponent(CenteredSpinner)),
)
